import React, { useState } from "react"
import { Link, Script } from "gatsby"

import { CTAChevron } from "assets/helper/icons"

import { NavigationLinksOutput } from "contexts/settings/Navigation/navigation-types"
import { useGeneralSettings } from "hooks/useGeneralSettings"
import { ImageBuilder } from "_types/AssetsImage"

import { SocialIcons } from "components/social-icons/SocialIcons"
import { FooterCredits } from "./FooterCredits"
import { FooterLocation } from "./FooterLocation"
import "./footer.module.scss"
import { useMainOfficeNumber } from "hooks/useMainOfficeNumber"

interface FooterProps {
  logo: ImageBuilder
  creditsNavigation: NavigationLinksOutput[]
  footerNavigation: NavigationLinksOutput[]
  customTrackingNum?: string
  isLandingPage: boolean
}

export const Footer = ({ logo, creditsNavigation, customTrackingNum, isLandingPage = false }: FooterProps) => {
  const { mainOfficeNumber } = useMainOfficeNumber({
    hasDisabledCustomTrackingNumber: true,
  })

  const { customCTAText, socialMedia, formattedOffices, companyName,recapKey
  } =
    useGeneralSettings()

  const [viewAllAddresses, toggleViewAllAddresses] = useState(false)

  const isSingleOffice = formattedOffices.length === 1

  const showAllAddresses = viewAllAddresses ? "--active" : ""

  const mainOffice = formattedOffices[0],
    mainOfficeAddress = `${mainOffice.address_line_1}` + (mainOffice.address_line_1.length > 0 ? ', ' : '') + `${mainOffice.city}, ${mainOffice.state} ${mainOffice.zip}`

  const handleViewAllAddresses = () => {
    let currentState = viewAllAddresses
    toggleViewAllAddresses(!currentState)
  }
  return (
    <footer className="fr-footer">
      <div className="fr-container fr-container--small fr-footer__inner">
        <div className="fr-footer__logo">
          <Link
            aria-label={`Go to ${companyName} Homepage`}
            className="base-logo fr-logo__item"
            to="/"
          >
            <img
              alt={logo.description || `${companyName} Logo`}
              loading="lazy"
              src={logo.url}
            />
          </Link>
        </div>

        <div className="fr-footer__cta-phone">
          <p className="fr-cta__item">{customCTAText}</p>
          {mainOfficeNumber && (
            <a href={`tel: ${(isLandingPage && customTrackingNum) ? customTrackingNum : mainOfficeNumber}`} className="fr-phone__item">
              {(isLandingPage && customTrackingNum) ? customTrackingNum : mainOfficeNumber}
            </a>
          )}
        </div>

        <div className="fr-footer__addresses">
          <div className="fr-addresses__details">
            <span className="fr-addresses__item--main">
              {mainOfficeAddress}
            </span>
            <span className="fr-addresses__item--main">
              {mainOffice.phone}
            </span>
            {!isSingleOffice && (
              <span
                className={`fr-addresses__toggle${showAllAddresses}`}
                onClick={() => handleViewAllAddresses()}
              >
                {viewAllAddresses ? "Hide Additional" : "View More"} Office Locations{" "}
                <CTAChevron />
              </span>
            )}
          </div>
          {!isSingleOffice && (
            <div className={`fr-addresses__list${showAllAddresses}`}>
              <div className={`fr-addresses__list__${formattedOffices.length <= 2 ? 'single-active-office-wrapper' : 'wrapper'}`}>
                {formattedOffices.slice(1).map(office => (
                  <FooterLocation
                    classModifier="__item"
                    key={office.id}
                    office={office}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {socialMedia.length > 0 && (
          <div className="fr-footer__socials">
            {socialMedia.map(
              ({ linkUrl, mediaOutlet, customIcon, customOutletName, id }) => {
                return (
                  <SocialIcons
                    customIcon={customIcon}
                    customOutletName={customOutletName}
                    href={linkUrl}
                    key={id}
                    socialMediaOutlet={mediaOutlet}
                    whereAmI="--footer"
                  />
                )
              }
            )}
          </div>
        )}
      </div>

      <div className="fr-container fr-footer__credits">
        <FooterCredits creditsNavigation={creditsNavigation} />
      </div>
      <Script id="recaptcha" src={`https://www.google.com/recaptcha/api.js?render=${recapKey}`} strategy="idle" />
    </footer>
  )
}
